import React, { useEffect, useState } from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { markSyncStartedThisSession } from 'components/spreadsheet/helpers/syncSession'
import api from 'helpers/api'
import Button from 'components/button'
import Spinner from 'components/spinner'

// Poll quickly at first so small (e.g. single-row) imports clear the pill almost immediately,
// then back off towards a slower cadence for long-running imports to avoid hammering the API.
const POLL_INTERVAL_MIN_MS = 1000
const POLL_INTERVAL_MAX_MS = 5000
const POLL_BACKOFF_FACTOR = 1.6

/**
 * Non-blocking indicator for an in-flight external data source import. External syncs run
 * as a background job, so a freshly-synced table (e.g. one created via the "Create from
 * External Data Sources" flow) starts empty and only fills once the job completes. While
 * `tableDetails.isSyncing` is set this polls the sync-info endpoint and shows a small
 * "import in progress" pill — deliberately not an overlay, so the user can keep working on
 * the table. When the import finishes it swaps to a "complete" pill with a reload button so
 * the user can pull in the freshly imported rows when they're ready — unless the table
 * already has rows loaded, in which case the data is on screen and the banner is skipped.
 */
const SyncStatus: React.FC = () => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const isSyncing = spreadsheetData.tableDetails.isSyncing
  const tableId = spreadsheetData.tableDetails.publicId
  const hasRowsLoaded = spreadsheetData.totalRows > 0
  const [justCompleted, setJustCompleted] = useState(false)

  // Clear any stale completion state when switching tables. The polling effect below only
  // resets `justCompleted` when a sync is running, so without this a previous table's
  // "Import complete" banner would leak onto the next (non-syncing) table if this component
  // stays mounted across the navigation.
  useEffect(() => {
    setJustCompleted(false)
  }, [tableId])

  useEffect(() => {
    if (!isSyncing || !tableId) {
      return
    }

    // Remember that this table has synced this session so the row-adding controls stay
    // hidden after the import completes (until the user reloads to see the imported data).
    markSyncStartedThisSession(tableId)
    setJustCompleted(false)

    let cancelled = false
    let timeout: ReturnType<typeof setTimeout>
    let delay = POLL_INTERVAL_MIN_MS

    const scheduleNext = () => {
      timeout = setTimeout(poll, delay)
      delay = Math.min(delay * POLL_BACKOFF_FACTOR, POLL_INTERVAL_MAX_MS)
    }

    const poll = () => {
      api
        .getSyncIntegrationInfo(tableId)
        .then((res) => {
          if (cancelled) {
            return
          }
          if (res?.data?.isSyncing === false) {
            setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'isSyncing', value: false })
            setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'lastSync', value: res.data.lastSync })
            setSpreadsheetData({
              type: 'UPDATE_TABLE',
              field: 'failedSyncAttempts',
              value: res.data.failedSyncAttempts
            })
            setJustCompleted(true)
            return
          }
          scheduleNext()
        })
        .catch(() => {
          // Transient poll failures are ignored; retry on the next tick.
          if (!cancelled) {
            scheduleNext()
          }
        })
    }

    // Check straight away so a fast import isn't held behind a full poll interval, then poll
    // on the backing-off schedule until it reports complete.
    poll()

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [isSyncing, tableId, setSpreadsheetData])

  if (isSyncing) {
    return (
      <div
        data-testid="sync-status-in-progress"
        className="flex w-full items-center gap-2 border-b border-divider bg-really-light-blue py-2 px-4 text-sm text-muted"
      >
        <Spinner size="small" />
        <span>Import in progress. You&apos;ll be emailed when it&apos;s complete.</span>
      </div>
    )
  }

  if (justCompleted && !hasRowsLoaded) {
    return (
      <div
        data-testid="sync-status-complete"
        className="flex w-full items-center gap-3 border-b border-divider bg-light-green py-2 px-4 text-sm text-muted"
      >
        <span>Import complete.</span>
        <Button className="ml-auto" internalType="accept" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    )
  }

  return null
}

export default SyncStatus
